import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { useToastStore } from '@/stores/useToastStore';
import { AuthView } from './AuthPopover';
import { Separator } from '@/components/ui/separator';
import { GoogleLogin } from '@/components/auth/GoogleLogin';
import { FacebookLogin } from '@/components/auth/FacebookLogin';
import { Sparkles, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const formSchema = z.object({
  email: z.string().email({
    message: 'Please enter a valid email address.',
  }),
  password: z.string().min(6, {
    message: 'Password must be at least 6 characters.',
  }),
});

interface AuthSignInProps {
  onViewChange: (view: AuthView) => void;
  onSuccess: () => void;
}

const AuthSignIn = ({ onViewChange, onSuccess }: AuthSignInProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const addToast = useToastStore((state) => state.addToast);
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsLoading(true);
      await login({ email: values.email, password: values.password });
      addToast({
        title: 'Success',
        description: 'Successfully signed in.',
        type: 'success',
      });
      onSuccess();
    } catch (error: any) {
      addToast({
        title: 'Error',
        description: error?.message || 'Invalid email or password.',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          Welcome back
        </h1>
        <p className="text-sm text-muted-foreground">
          Enter your email to sign in to your account
        </p>
      </div>
      <div className="grid gap-2">
        <GoogleLogin onSuccess={onSuccess} />
        <FacebookLogin onSuccess={onSuccess} />
      </div>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            Or continue with email
          </span>
        </div>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    placeholder="name@example.com"
                    type="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    placeholder="Enter your password"
                    type="password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? (
              <Upload className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Sign In
          </Button>
        </form>
      </Form>
      <p className="text-center text-sm text-muted-foreground">
        Don't have an account?{' '}
        <button
          className="underline underline-offset-4 hover:text-primary"
          onClick={() => navigate('/signup')}
        >
          Sign up
        </button>
      </p>
    </div>
  );
};

export default AuthSignIn;
